import { css, styled } from "@mui/material";
import { HierarcicalObjectReference } from "@novorender/data-js-api";
import { ChevronRight, Eye, EyeOff } from "@procore/core-icons";
import { Box, Checkbox, colors, Dropdown, Search, Typography } from "@procore/core-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { Node, Tree } from "components/objectTree/types";
import { BaseWindowHeader } from "components/window/BaseWindowHeader";
import { HEADER_BORDER } from "components/window/constants";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { ModelTreeNodeData } from "features/modelTree/types";
import {
    renderActions,
    selectDefaultVisibility,
    selectMainObject,
    selectPreIsolationVisibility,
} from "features/render";
import { undoRedoActions } from "features/undoRedo";
import { useAbortController } from "hooks/useAbortController";
import {
    hideObjectHierarchy,
    hideObjectHierarchyFromFilteredTree,
    selectObjectHierarchy,
    selectObjectHierarchyFromFilteredTree,
    showObjectHierarchy,
    showObjectHierarchyFromFilteredTree,
    unselectAll,
    unselectObjectHierarchy,
    unselectObjectHierarchyFromFilteredTree,
} from "utils/objectActions";
import { GroupState } from "utils/treeHelpers";

import { CHEVRON_SIZE } from "../constants";
import { createBulkActions } from "../utils/bulkActions";
import { VisibilityButton } from "./VisibilityButton";

interface ObjectTreeWindowHeaderProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    onSearchSubmit: () => void;
    onCollapseAll: () => void;
    tree: Tree<ModelTreeNodeData>;
    filteredObjectIds: Set<number> | null;
    hasNoResults: boolean;
    onClose?: () => void;
}

const ChevronButton = styled(Box)<{ disabled?: boolean; collapsed?: boolean }>(
    ({ disabled, collapsed }) => css`
        height: ${CHEVRON_SIZE}px;
        cursor: ${disabled ? "not-allowed" : "pointer"};
        transform: rotate(${collapsed ? "0deg" : "90deg"});
        transition: transform 0.2s ease;
        border-radius: 4px;
        display: flex;
        align-items: center;
        opacity: ${disabled ? 0.4 : 1};

        &:hover {
            background-color: ${disabled ? "transparent" : colors.gray90};
        }
    `,
);

/**
 * Generic helper to calculate the group state (all/some/none) of a set of IDs
 * relative to the tree structure.
 * Used for both selection and visibility states.
 */
function getRootGroupState(
    rootNode: Node<ModelTreeNodeData>,
    tree: Tree<ModelTreeNodeData>,
    ids: Record<number, true | undefined>,
    filteredObjectIds: Set<number> | null = null,
): GroupState {
    const idCount = Object.keys(ids).length;

    if (idCount === 0) {
        return "none";
    }

    // For unfiltered trees: Check if all top-level items (folders and leaves) are in the set
    if (rootNode.children && filteredObjectIds === null) {
        let allTopLevelItemsInSet = true;
        for (const childPath of rootNode.children) {
            const child = tree[childPath];
            if (child && ids[child.data.object.id] !== true) {
                allTopLevelItemsInSet = false;
                break;
            }
        }

        if (allTopLevelItemsInSet) {
            return "all";
        }
    }

    // For filtered trees: Check if all filtered IDs are in the set
    if (filteredObjectIds !== null) {
        const totalFiltered = filteredObjectIds.size;
        if (totalFiltered === 0) {
            return "none";
        }

        let idsInSetCount = 0;
        for (const id of filteredObjectIds) {
            if (ids[id] === true) {
                idsInSetCount++;
            }
        }

        if (idsInSetCount === totalFiltered) {
            return "all";
        }
    }

    return "some";
}

/** Appends without spread — `push(...ids)` throws when ids.length exceeds ~65k (max arguments). */
function appendNumberIds(target: number[], ids: number[]): void {
    for (let i = 0; i < ids.length; i++) {
        target.push(ids[i]);
    }
}

/**
 * Extract top-level objects from the tree structure.
 */
function getTopLevelObjectsFromTree(
    rootNode: Node<ModelTreeNodeData>,
    tree: Tree<ModelTreeNodeData>,
): HierarcicalObjectReference[] {
    if (!rootNode.children || rootNode.children.length === 0) {
        return rootNode.data?.object ? [rootNode.data.object] : [];
    }

    return rootNode.children
        .map((childPath) => tree[childPath])
        .filter((node) => node?.data?.object)
        .map((node) => node.data.object);
}

export function ObjectTreeWindowHeader({
    searchTerm,
    onSearchChange,
    onSearchSubmit,
    onCollapseAll,
    tree,
    filteredObjectIds,
    hasNoResults,
    onClose,
}: ObjectTreeWindowHeaderProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const dispatchHidden = useDispatchHidden();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const { flyToSelected } = useFlyToSelected();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const mainObject = useAppSelector(selectMainObject);
    const highlightedSet = useHighlighted();
    const { idArr: highlightedIds, ids: highlightedIdsRecord } = highlightedSet;
    const hiddenSet = useHidden();
    const { ids: hiddenIdsRecord, lastHiddenIds } = hiddenSet;
    const {
        state: { db },
    } = useExplorerGlobals(true);
    const [isLoading, setIsLoading] = useState(false);
    const [abortController, abortSelection] = useAbortController();

    const selectedCount = highlightedIds.length;
    const hasSelection = selectedCount > 0;
    const isNoResultsWithoutSelection = hasNoResults && !hasSelection;
    const isCheckboxDisabled = isLoading || isNoResultsWithoutSelection;
    const isVisibilityDisabled = isLoading || isNoResultsWithoutSelection;

    const rootSelectionState = useMemo(() => {
        const rootNode = tree[""];
        if (!rootNode) {
            return "none" as const;
        }

        return getRootGroupState(rootNode, tree, highlightedIdsRecord, filteredObjectIds);
    }, [tree, filteredObjectIds, highlightedIdsRecord]);

    const rootVisibilityState = useMemo(() => {
        const rootNode = tree[""];
        if (!rootNode) {
            return "none" as const;
        }

        return getRootGroupState(rootNode, tree, hiddenIdsRecord, filteredObjectIds);
    }, [tree, filteredObjectIds, hiddenIdsRecord]);

    const selectedVisibilityState = useMemo((): GroupState => {
        if (!hasSelection) {
            return "none";
        }

        let allHidden = true;
        let anyHidden = false;

        for (const id of highlightedIds) {
            if (hiddenIdsRecord[id] === true) {
                anyHidden = true;
            } else {
                allHidden = false;
            }
            if (anyHidden && !allHidden) {
                break;
            }
        }

        if (allHidden) {
            return "all";
        }
        if (anyHidden) {
            return "some";
        }
        return "none";
    }, [hasSelection, highlightedIds, hiddenIdsRecord]);

    const effectiveVisibilityState = hasSelection ? selectedVisibilityState : rootVisibilityState;

    // Check if tree is fully collapsed by checking if any top-level nodes are expanded
    // If all root children are collapsed, the tree appears fully collapsed to the user
    let isFullyCollapsed = true;
    const rootNode = tree[""];
    if (rootNode?.children) {
        for (const childPath of rootNode.children) {
            const child = tree[childPath];
            if (child?.expanded) {
                isFullyCollapsed = false;
                break;
            }
        }
    }

    const handleGlobalCheckboxChange = async () => {
        if (isLoading) {
            return;
        }

        if (hasNoResults) {
            if (!hasSelection) {
                return;
            }

            unselectAll({
                dispatch,
                dispatchHighlighted,
                currentHighlightedIds: highlightedIds,
                currentMainObject: mainObject,
            });
            return;
        }

        if (!db) {
            return;
        }

        const rootNode = tree[""];
        if (!rootNode) {
            return;
        }

        const objects = getTopLevelObjectsFromTree(rootNode, tree);
        if (objects.length === 0) {
            return;
        }

        if (rootSelectionState !== "all") {
            void track(Events.objectTree.selectAll);
        }

        // Abort any in-progress selection operation
        abortSelection();

        const isUnselect = rootSelectionState === "all";

        const runForEachRoot = async (
            action: (obj: HierarcicalObjectReference) => Promise<number[]>,
        ): Promise<number[]> => {
            const all: number[] = [];
            for (const obj of objects) {
                appendNumberIds(all, await action(obj));
            }
            return Array.from(new Set(all));
        };

        try {
            let affectedIds: number[];
            setIsLoading(true);
            try {
                if (filteredObjectIds != null) {
                    affectedIds = await runForEachRoot((obj) =>
                        isUnselect
                            ? unselectObjectHierarchyFromFilteredTree({
                                  nodes: [obj],
                                  filteredObjectIds,
                                  dispatchHighlighted,
                                  tree,
                              })
                            : // Note: dispatchHidden is intentionally NOT passed here.
                              // Objects can exist in both selected and hidden sets simultaneously.
                              selectObjectHierarchyFromFilteredTree({
                                  nodes: [obj],
                                  filteredObjectIds,
                                  dispatchHighlighted,
                                  tree,
                              }),
                    );
                } else {
                    affectedIds = await runForEachRoot((obj) =>
                        isUnselect
                            ? unselectObjectHierarchy({
                                  nodes: [obj],
                                  db,
                                  abortSignal: abortController.current.signal,
                                  setLoading: () => {},
                                  dispatchHighlighted,
                              })
                            : // Note: dispatchHidden is intentionally NOT passed here.
                              // Objects can exist in both selected and hidden sets simultaneously.
                              selectObjectHierarchy({
                                  nodes: [obj],
                                  db,
                                  abortSignal: abortController.current.signal,
                                  setLoading: () => {},
                                  dispatchHighlighted,
                              }),
                    );
                }
            } finally {
                setIsLoading(false);
            }

            if (affectedIds.length > 0) {
                const clearedMainObjectId =
                    isUnselect && mainObject !== undefined && affectedIds.includes(mainObject) ? mainObject : undefined;

                if (clearedMainObjectId !== undefined) {
                    dispatch(renderActions.setMainObject(undefined));
                }

                dispatch(
                    undoRedoActions.push({
                        description: isUnselect ? "Unselect objects" : "Select objects",
                        undoCallback: () => {
                            dispatchHighlighted(
                                isUnselect ? highlightActions.add(affectedIds) : highlightActions.remove(affectedIds),
                            );
                            if (clearedMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(clearedMainObjectId));
                            }
                        },
                        redoCallback: () => {
                            dispatchHighlighted(
                                isUnselect ? highlightActions.remove(affectedIds) : highlightActions.add(affectedIds),
                            );
                            if (clearedMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(undefined));
                            }
                        },
                    }),
                );
            }
        } catch (error) {
            if (!abortController.current.signal.aborted) {
                console.error("Global selection failed:", error);
            }
        }
    };

    const handleGlobalVisibilityToggle = async () => {
        if (isLoading || !db) {
            return;
        }

        if (isNoResultsWithoutSelection) {
            return;
        }

        if (hasSelection) {
            const isUnhide = effectiveVisibilityState === "all";
            const ids = highlightedIds;

            if (ids.length === 0) {
                return;
            }

            if (effectiveVisibilityState !== "all") {
                void track(Events.objectTree.hideAll);
            }

            if (isUnhide) {
                dispatchHidden(hiddenActions.remove(ids));
            } else {
                dispatchHidden(hiddenActions.add(ids));
                dispatchHidden(hiddenActions.setLastHiddenIds(ids));
            }

            dispatch(
                undoRedoActions.push({
                    description: isUnhide ? "Show objects" : "Hide objects",
                    undoCallback: () => dispatchHidden(isUnhide ? hiddenActions.add(ids) : hiddenActions.remove(ids)),
                    redoCallback: () => dispatchHidden(isUnhide ? hiddenActions.remove(ids) : hiddenActions.add(ids)),
                }),
            );
            return;
        }

        const rootNode = tree[""];
        if (!rootNode) {
            return;
        }

        const objects = getTopLevelObjectsFromTree(rootNode, tree);
        if (objects.length === 0) {
            return;
        }

        if (effectiveVisibilityState !== "all") {
            void track(Events.objectTree.hideAll);
        }

        // Abort any in-progress operation
        abortSelection();

        try {
            setIsLoading(true);
            let affectedIds: number[];
            if (filteredObjectIds != null) {
                const allAffectedIds: number[] = [];
                for (const obj of objects) {
                    const ids =
                        effectiveVisibilityState === "all"
                            ? await showObjectHierarchyFromFilteredTree({
                                  nodes: [obj],
                                  filteredObjectIds,
                                  dispatchHidden,
                                  tree,
                              })
                            : await hideObjectHierarchyFromFilteredTree({
                                  nodes: [obj],
                                  filteredObjectIds,
                                  dispatchHidden,
                                  tree,
                              });
                    appendNumberIds(allAffectedIds, ids);
                }
                affectedIds = Array.from(new Set(allAffectedIds));
            } else {
                const allAffectedIds: number[] = [];
                for (const obj of objects) {
                    const ids =
                        effectiveVisibilityState === "all"
                            ? await showObjectHierarchy({
                                  nodes: [obj],
                                  db,
                                  abortSignal: abortController.current.signal,
                                  setLoading: () => {},
                                  dispatchHidden,
                              })
                            : await hideObjectHierarchy({
                                  nodes: [obj],
                                  db,
                                  abortSignal: abortController.current.signal,
                                  setLoading: () => {},
                                  dispatchHidden,
                              });
                    appendNumberIds(allAffectedIds, ids);
                }
                affectedIds = Array.from(new Set(allAffectedIds));
            }
            if (affectedIds.length > 0) {
                const isShow = effectiveVisibilityState === "all";
                dispatch(
                    undoRedoActions.push({
                        description: isShow ? "Show objects" : "Hide objects",
                        undoCallback: () =>
                            dispatchHidden(isShow ? hiddenActions.add(affectedIds) : hiddenActions.remove(affectedIds)),
                        redoCallback: () =>
                            dispatchHidden(isShow ? hiddenActions.remove(affectedIds) : hiddenActions.add(affectedIds)),
                    }),
                );
            }
        } catch (error) {
            if (!abortController.current.signal.aborted) {
                console.error("Global visibility toggle failed:", error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const actions = createBulkActions(selectedCount, highlightedIds, {
        t,
        dispatch,
        dispatchHighlighted,
        dispatchHidden,
        dispatchSelectionBasket,
        defaultVisibility,
        preIsolationVisibility,
        flyToSelected,
        currentMainObject: mainObject,
        hiddenIds: hiddenIdsRecord,
        lastHiddenIds: lastHiddenIds,
    });

    return (
        <BaseWindowHeader title={t("objectTree")} onClose={onClose}>
            <Box
                style={{
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    paddingBottom: "8px",
                    paddingTop: "8px",
                    borderBottom: HEADER_BORDER,
                }}
            >
                <Search
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder={t("filterByKeyword")}
                    autoComplete="off"
                    onSubmit={onSearchSubmit}
                />
            </Box>

            <Box
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    borderBottom: HEADER_BORDER,
                    backgroundColor: hasSelection ? colors.blue96 : colors.white,
                    transition: "background-color 0.2s ease",
                }}
            >
                <Checkbox
                    checked={rootSelectionState === "all"}
                    indeterminate={rootSelectionState === "some"}
                    onChange={handleGlobalCheckboxChange}
                    disabled={isCheckboxDisabled}
                    aria-label={t("selectAll")}
                    data-testid="global-checkbox"
                />

                <ChevronButton
                    disabled={isFullyCollapsed}
                    collapsed={isFullyCollapsed}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!isFullyCollapsed) {
                            onCollapseAll();
                        }
                    }}
                    role="button"
                    aria-label={t("collapseAll")}
                >
                    <ChevronRight style={{ color: colors.gray60 }} />
                </ChevronButton>

                {hasSelection ? (
                    <>
                        <Dropdown
                            label={t("actions")}
                            variant="tertiary"
                            size="sm"
                            data-testid="bulk-actions-dropdown-button"
                            onSelect={({ item }) => {
                                const action = actions.find((a) => a.id === item);
                                if (action && !action.disabled) {
                                    action.onClick();
                                }
                            }}
                        >
                            {actions.map((action) => (
                                <Dropdown.Item
                                    key={action.id}
                                    item={action.id}
                                    disabled={action.disabled}
                                    data-testid={`bulk-action-${action.id}`}
                                >
                                    {action.label}
                                </Dropdown.Item>
                            ))}
                        </Dropdown>

                        <Box style={{ flex: 1 }} />

                        <Typography style={{ fontSize: "13px", color: colors.gray15 }}>
                            {filteredObjectIds !== null
                                ? t("propertiesOfTotal", { selectedCount, totalCount: filteredObjectIds.size })
                                : t("selectedCount", { count: selectedCount })}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Box style={{ flex: 1 }} />
                        {filteredObjectIds !== null && (
                            <Typography style={{ fontSize: "13px", color: colors.gray15 }}>
                                {t("objectCount", { count: filteredObjectIds.size })}
                            </Typography>
                        )}
                    </>
                )}

                <VisibilityButton
                    disabled={isVisibilityDisabled}
                    onClick={(e) => {
                        if (isVisibilityDisabled) {
                            return;
                        }
                        e.stopPropagation();
                        handleGlobalVisibilityToggle();
                    }}
                    role="button"
                >
                    {effectiveVisibilityState === "all" ? (
                        <EyeOff style={{ width: 16, height: 16 }} />
                    ) : (
                        <Eye style={{ width: 16, height: 16 }} />
                    )}
                </VisibilityButton>
            </Box>
        </BaseWindowHeader>
    );
}
